import { useState, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, ArrowRight } from 'lucide-react';
import {
  TERMS_OPTIONS,
  TERMS_TITLE_LINE_1,
  TERMS_TITLE_LINE_2,
  TERMS_SUBTITLE,
} from '@miles/shared';
import { API_URL } from '../lib/api';
import { AnimatedBackground } from '../components/AnimatedBackground';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';

// Checkbox copy + linked documents live in @miles/shared (TERMS_OPTIONS) so the
// cloud dashboard and the plugin setup wizard stay in lockstep.

export function TermsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Animation states
  const [displayedTitleLine1, setDisplayedTitleLine1] = useState('');
  const [displayedTitleLine2, setDisplayedTitleLine2] = useState('');
  const [displayedSubtitle, setDisplayedSubtitle] = useState('');
  const [visibleOptions, setVisibleOptions] = useState(0);
  const [showButton, setShowButton] = useState(false);

  // Animation refs
  const titleLine1CharIndex = useRef(0);
  const titleLine2CharIndex = useRef(0);
  const subtitleCharIndex = useRef(0);
  // The stream functions reschedule themselves with untracked setTimeouts;
  // this flag stops the chains on unmount so they don't keep firing setState
  // against an unmounted component.
  const animationCancelledRef = useRef(false);
  const [titleLine1Complete, setTitleLine1Complete] = useState(false);
  const [titleLine2Complete, setTitleLine2Complete] = useState(false);
  const [subtitleComplete, setSubtitleComplete] = useState(false);

  const TITLE_LINE1 = TERMS_TITLE_LINE_1;
  const TITLE_LINE2 = TERMS_TITLE_LINE_2;
  const SUBTITLE = TERMS_SUBTITLE;

  // Animation sequence
  useEffect(() => {
    animationCancelledRef.current = false;
    titleLine1CharIndex.current = 0;
    titleLine2CharIndex.current = 0;
    subtitleCharIndex.current = 0;
    setDisplayedTitleLine1('');
    setDisplayedTitleLine2('');
    setDisplayedSubtitle('');
    setTitleLine1Complete(false);
    setTitleLine2Complete(false);
    setSubtitleComplete(false);
    setVisibleOptions(0);
    setShowButton(false);

    const titleTimer = setTimeout(() => {
      streamTitleLine1();
    }, 300);

    return () => {
      animationCancelledRef.current = true;
      clearTimeout(titleTimer);
    };
  }, []);

  const streamTitleLine1 = () => {
    if (animationCancelledRef.current) return;
    if (titleLine1CharIndex.current >= TITLE_LINE1.length) {
      setTitleLine1Complete(true);
      return;
    }

    titleLine1CharIndex.current = Math.min(
      titleLine1CharIndex.current + 3,
      TITLE_LINE1.length
    );
    setDisplayedTitleLine1(TITLE_LINE1.slice(0, titleLine1CharIndex.current));

    const currentChar = TITLE_LINE1[titleLine1CharIndex.current - 1];
    let delay = 28;
    if (currentChar === '!' || currentChar === '?' || currentChar === '.') {
      delay = 120;
    } else if (currentChar === ',') {
      delay = 64;
    } else if (currentChar === ' ') {
      delay = 20;
    }

    setTimeout(streamTitleLine1, delay);
  };

  useEffect(() => {
    if (!titleLine1Complete) return;

    const timer = setTimeout(() => {
      streamTitleLine2();
    }, 200);

    return () => clearTimeout(timer);
  }, [titleLine1Complete]);

  const streamTitleLine2 = () => {
    if (animationCancelledRef.current) return;
    if (titleLine2CharIndex.current >= TITLE_LINE2.length) {
      setTitleLine2Complete(true);
      return;
    }

    titleLine2CharIndex.current = Math.min(
      titleLine2CharIndex.current + 3,
      TITLE_LINE2.length
    );
    setDisplayedTitleLine2(TITLE_LINE2.slice(0, titleLine2CharIndex.current));

    const currentChar = TITLE_LINE2[titleLine2CharIndex.current - 1];
    let delay = 28;
    if (currentChar === '!' || currentChar === '?' || currentChar === '.') {
      delay = 120;
    } else if (currentChar === ',') {
      delay = 64;
    } else if (currentChar === ' ') {
      delay = 20;
    }

    setTimeout(streamTitleLine2, delay);
  };

  useEffect(() => {
    if (!titleLine2Complete) return;

    const subtitleTimer = setTimeout(() => {
      streamSubtitle();
    }, 400);

    return () => clearTimeout(subtitleTimer);
  }, [titleLine2Complete]);

  const streamSubtitle = () => {
    if (animationCancelledRef.current) return;
    if (subtitleCharIndex.current >= SUBTITLE.length) {
      setSubtitleComplete(true);
      return;
    }

    subtitleCharIndex.current = Math.min(
      subtitleCharIndex.current + 3,
      SUBTITLE.length
    );
    setDisplayedSubtitle(SUBTITLE.slice(0, subtitleCharIndex.current));

    const currentChar = SUBTITLE[subtitleCharIndex.current - 1];
    let delay = 28;
    if (currentChar === ':') {
      delay = 100;
    } else if (currentChar === ' ') {
      delay = 20;
    }

    setTimeout(streamSubtitle, delay);
  };

  useEffect(() => {
    if (!subtitleComplete) return;

    const optionTimers = TERMS_OPTIONS.map((_, i) =>
      setTimeout(() => setVisibleOptions(i + 1), 300 + i * 200)
    );

    const buttonTimer = setTimeout(
      () => {
        setShowButton(true);
      },
      300 + TERMS_OPTIONS.length * 200 + 100
    );

    return () => {
      optionTimers.forEach(clearTimeout);
      clearTimeout(buttonTimer);
    };
  }, [subtitleComplete]);

  const handleToggle = (id: string) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAccept = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/v2/cloud/accept-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          acceptedAll: true,
          acceptedAuditConfirmation: true,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept terms');
      }

      navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const allChecked = TERMS_OPTIONS.every((opt) => checkedItems.has(opt.id));
  const canSubmit = allChecked && !isSubmitting;

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-xl">
        {/* Title Line 1 */}
        <div className="mb-2">
          <h1 className="text-2xl font-semibold text-white">
            {displayedTitleLine1}
            {!titleLine1Complete && displayedTitleLine1 && (
              <span className="ml-0.5 inline-block h-6 w-0.5 animate-pulse bg-white/60" />
            )}
          </h1>
        </div>

        {/* Title Line 2 — lighter/smaller so it reads as a subheading under
            the greeting rather than a second equally-weighted headline. */}
        <div className="mb-6">
          <p className="text-xl font-normal text-white/60">
            {displayedTitleLine2}
            {titleLine1Complete && !titleLine2Complete && displayedTitleLine2 && (
              <span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-white/40" />
            )}
          </p>
        </div>

        {/* Subtitle */}
        <div className="mb-6">
          <p className="text-lg font-semibold text-white">
            {displayedSubtitle}
            {titleLine2Complete && !subtitleComplete && displayedSubtitle && (
              <span className="ml-0.5 inline-block h-5 w-0.5 animate-pulse bg-white/60" />
            )}
          </p>
        </div>

        {/* Checkboxes */}
        <div className="space-y-4">
          {TERMS_OPTIONS.map((option, index) => {
            const isVisible = index < visibleOptions;
            const isChecked = checkedItems.has(option.id);

            const linkElement =
              option.url && option.linkText ? (
                <a
                  href={option.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-white font-semibold underline underline-offset-2 hover:text-white/80"
                >
                  {option.linkText}
                </a>
              ) : null;

            return (
              <div
                key={option.id}
                className={`transition-all duration-300 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
              >
                <div className="rounded-xl bg-white/[0.07] border border-white/[0.08] p-4">
                  <button
                    type="button"
                    onClick={() => handleToggle(option.id)}
                    disabled={isSubmitting}
                    className={`flex w-full items-center gap-4 text-left ${
                      isSubmitting ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  >
                    {/* Circular Checkbox */}
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        isChecked
                          ? 'border-white bg-white'
                          : 'border-white/40 bg-transparent hover:border-white/60'
                      }`}
                    >
                      {isChecked && (
                        <Check className="h-4 w-4 text-primary-foreground" strokeWidth={3} />
                      )}
                    </div>

                    {/* Label */}
                    <span className="text-base text-white/90 leading-relaxed">
                      {option.textBefore}
                      {linkElement}
                      {option.textAfter}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Submit button */}
        <div
          className={`mt-6 flex justify-end transition-all duration-300 ${
            showButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          <Button
            onClick={handleAccept}
            disabled={!canSubmit}
            size="lg"
          >
            {isSubmitting ? 'Setting up...' : 'Accept and Continue'}
            {!isSubmitting && <ArrowRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
